import { Injectable, signal, inject } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";

import { ScheduledEmail } from "src/app/api/models/scheduled-email";
import { EmailsService } from "src/app/api/services/emails.service";
import { ModalService } from "src/app/shared/services/modal.service";

/**
 * Service for managing scheduled emails in the Meetday modal.
 * This service is tab-specific and handles all email-related operations.
 */
@Injectable()
export class MeetdayTabEmailsService {
  private fb = inject(FormBuilder);
  private emailsService = inject(EmailsService);
  private modal = inject(ModalService);

  // ========== State ==========
  scheduledEmails = signal<ScheduledEmail[]>([]);

  // ========== Form ==========
  emailForm: FormGroup = this.fb.group({
    emailSubject: ["", [Validators.required]],
    emailBody: ["", [Validators.required]],
    scheduledDateTime: ["", [Validators.required]],
  });

  // ========== API Operations ==========

  /**
   * Load all scheduled emails from the API.
   */
  loadScheduledEmails(): void {
    this.emailsService.getScheduledEmails().subscribe((emails) => {
      this.scheduledEmails.set(emails || []);
    });
  }

  /**
   * Save an email (create or update).
   * @param email The email data to save
   * @param isNew Whether this is a new email or an update
   * @returns Observable<boolean> indicating success
   */
  saveEmail(email: ScheduledEmail, isNew: boolean): Observable<boolean> {
    if (isNew) {
      return this.emailsService.createScheduledEmail({ body: email }).pipe(
        map(() => {
          this.loadScheduledEmails();
          return true;
        }),
        catchError((error) => {
          console.error("Failed to create scheduled email:", error);
          this.modal.Alert(
            "Error",
            "Failed to create scheduled email. Please try again."
          );
          return of(false);
        })
      );
    } else if (email.id) {
      const updateData: ScheduledEmail = {
        emailSubject: email.emailSubject,
        emailBody: email.emailBody,
        scheduledDateTime: email.scheduledDateTime,
        meetDayId: email.meetDayId,
      };

      return this.emailsService
        .updateScheduledEmail({ id: email.id, body: updateData })
        .pipe(
          map(() => {
            this.loadScheduledEmails();
            return true;
          }),
          catchError((error) => {
            console.error("Failed to update scheduled email:", error);
            this.modal.Alert(
              "Error",
              "Failed to update scheduled email. Please try again."
            );
            return of(false);
          })
        );
    }

    return of(false);
  }

  /**
   * Delete a scheduled email after user confirmation.
   * @param emailId The ID of the email to delete
   */
  deleteScheduledEmail(emailId: string): void {
    if (!emailId) {
      return;
    }

    this.modal
      .Confirm(
        "Delete Email",
        "Are you sure you want to delete this scheduled email?"
      )
      .then((confirmed) => {
        if (confirmed === true) {
          this.emailsService
            .deleteScheduledEmail({ id: emailId })
            .subscribe(() => {
              this.loadScheduledEmails();
            });
        }
      })
      .catch(() => {
        // User cancelled
      });
  }

  // ========== Form Helpers ==========

  /**
   * Reset the email form to empty values.
   */
  resetForm(): void {
    this.emailForm.patchValue({
      emailSubject: "",
      emailBody: "",
      scheduledDateTime: "",
    });
  }

  /**
   * Check if the email form is valid.
   */
  isFormValid(): boolean {
    return this.emailForm.valid;
  }
}
